// ===========================================
// Strategy Tab Component
// ===========================================
// Strategic planning sections for project growth and monetization
// Replaces the old Notes tab with structured strategy fields

import React, { useState, useEffect } from 'react';
import { useProjects } from '../../context/ProjectContext';
import { Target, ArrowRight, Users, DollarSign, TrendingUp, Loader, Check } from 'lucide-react';
import './StrategyTab.scss';

// ARR level options matching the backend model
const ARR_LEVELS = ['$0', '$10k', '$100k', '$1m', '$10m', '$100m', '$1B+'];

const StrategyTab = ({ project }) => {
  const { updateProject } = useProjects();
  // Get project ID (handle both id and _id from MongoDB)
  const projectId = project._id || project.id;
  
  // ===========================================
  // State Management
  // ===========================================
  // Local state for editable strategy fields
  const [strategy, setStrategy] = useState({
    currentState: project.strategy?.currentState || '',
    nextSteps: project.strategy?.nextSteps || '',
    growthStrategy: project.strategy?.growthStrategy || '',
    monetizationStrategy: project.strategy?.monetizationStrategy || ''
  });
  // ARR level state (tied to Dev Costs & ARR analytics)
  const [arrLevel, setArrLevel] = useState(project.arrLevel || '$0');
  // UI state for save indicators
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeField, setActiveField] = useState(null);
  
  // ===========================================
  // Sync with Project Changes
  // ===========================================
  // Only sync when project ID changes (i.e., navigating to different project)
  // Don't sync on every project update to avoid overwriting user's unsaved edits
  useEffect(() => {
    setStrategy({
      currentState: project.strategy?.currentState || '',
      nextSteps: project.strategy?.nextSteps || '',
      growthStrategy: project.strategy?.growthStrategy || '',
      monetizationStrategy: project.strategy?.monetizationStrategy || ''
    });
    setArrLevel(project.arrLevel || '$0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id, project.id]);  // Only re-initialize when switching to a different project
  
  // ===========================================
  // Save Handler - Auto-saves on blur
  // ===========================================
  const handleSave = async (field, value) => {
    setSaving(true);
    
    // Build update payload based on field type
    const updateData = field === 'arrLevel' 
      ? { arrLevel: value }
      : { strategy: { ...project.strategy, [field]: value } };
    
    await updateProject(projectId, updateData);
    setSaving(false);
    // Show saved indicator briefly
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  
  // ===========================================
  // Field Change Handlers
  // ===========================================
  // Update local state on input change
  const handleFieldChange = (field, value) => {
    setStrategy(prev => ({ ...prev, [field]: value }));
  };
  
  // Save on blur (when user leaves field)
  const handleBlur = (field) => {
    setActiveField(null);
    handleSave(field, strategy[field]);
  };
  
  // Handle ARR level selection (immediate save)
  const handleArrChange = (value) => {
    setArrLevel(value);
    handleSave('arrLevel', value);
  };

  // ===========================================
  // Section Configuration
  // ===========================================
  // Define each strategy section with icon, title, and placeholder
  const sections = [
    {
      id: 'currentState',
      title: 'Project Current State',
      icon: Target,
      placeholder: 'Where is the project right now? What has been built? Current status, recent milestones achieved...',
      color: '#3b82f6' // Blue - represents current position
    },
    {
      id: 'nextSteps',
      title: 'Next Steps',
      icon: ArrowRight,
      placeholder: 'What are the immediate priorities? Action items for the next sprint, blockers to address...',
      color: '#8b5cf6' // Purple - represents forward motion
    },
    {
      id: 'growthStrategy',
      title: 'User Growth Strategy',
      icon: Users,
      placeholder: 'How will you acquire users? Marketing channels, viral loops, partnerships, content strategy...',
      color: '#22c55e' // Green - represents growth
    },
    {
      id: 'monetizationStrategy',
      title: 'Monetization Strategy',
      icon: DollarSign,
      placeholder: 'How will this make money? Pricing tiers, revenue model, target MRR, payment providers...',
      color: '#f59e0b' // Amber - represents revenue
    }
  ];

  // ===========================================
  // Render
  // ===========================================
  return (
    <div className="strategy-tab">
      {/* Save status indicator - shows saving/saved state */}
      <div className="strategy-tab__status">
        {saving && (
          <span className="strategy-tab__saving">
            <Loader size={14} className="animate-spin" /> Saving...
          </span>
        )}
        {saved && (
          <span className="strategy-tab__saved">
            <Check size={14} /> Saved
          </span>
        )}
      </div>
      
      {/* Strategy sections - each with icon, title, and textarea */}
      {sections.map((section) => (
        <div key={section.id} className="strategy-section">
          <div className="strategy-section__header">
            <section.icon size={18} style={{ color: section.color }} />
            <h3>{section.title}</h3>
          </div>
          <textarea
            className={`strategy-section__textarea ${activeField === section.id ? 'active' : ''}`}
            value={strategy[section.id]}
            onChange={(e) => handleFieldChange(section.id, e.target.value)}
            onFocus={() => setActiveField(section.id)}
            onBlur={() => handleBlur(section.id)}
            placeholder={section.placeholder}
            rows={4}
          />
        </div>
      ))}
      
      {/* ARR Input Section - ties to Dev Costs & ARR analytics */}
      <div className="strategy-section strategy-section--arr">
        <div className="strategy-section__header">
          <TrendingUp size={18} style={{ color: '#10b981' }} />
          <h3>Current ARR</h3>
          <span className="strategy-section__hint">
            (ties to Dev Costs & ARR per project)
          </span>
        </div>
        {/* ARR level selector buttons */}
        <div className="arr-selector">
          {ARR_LEVELS.map((level) => (
            <button
              key={level}
              className={`arr-selector__btn ${arrLevel === level ? 'active' : ''}`}
              onClick={() => handleArrChange(level)}
            >
              {level}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StrategyTab;

